import type { GameEvent, GameView, PlayerId } from "@numbet/engine";
import { z } from "zod";

/** Raumcodes: 6 Zeichen ohne leicht verwechselbare Zeichen (0/O, 1/I/L). */
export const ROOM_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const ROOM_CODE_LENGTH = 6;
export const roomCode = z
  .string()
  .transform((s) => s.trim().toUpperCase())
  .pipe(z.string().regex(new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`)));

export const playerName = z.string().trim().min(1).max(16);

/** Avatare aus dem Design (4b): Initialen oder eines von sieben Symbolen. */
export const AVATARS = ["initials", "circle", "triangle", "diamond", "hexagon", "plus", "ring", "dome"] as const;
export type AvatarId = (typeof AVATARS)[number];
export const avatarId = z.enum(AVATARS).default("initials");

// ---------------------------------------------------------------------------
// HTTP

export const createRoomRequest = z.object({ name: playerName, avatar: avatarId });
export const joinRoomRequest = z.object({ name: playerName, avatar: avatarId });

export interface SessionResponse {
  code: string;
  playerId: PlayerId;
  token: string;
}

export interface RoomInfoResponse {
  code: string;
  phase: GameView["phase"];
  playerCount: number;
  maxPlayers: number;
}

export interface ErrorResponse {
  error: string;
}

// ---------------------------------------------------------------------------
// WebSocket: Browser → Server

export const clientMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("start") }),
  z.object({ type: z.literal("draftEstimate"), value: z.number() }),
  z.object({ type: z.literal("submitEstimate"), value: z.number() }),
  z.object({ type: z.literal("fold") }),
  z.object({ type: z.literal("check") }),
  z.object({ type: z.literal("call") }),
  z.object({ type: z.literal("bet"), to: z.number().int() }),
  z.object({ type: z.literal("allIn") }),
  z.object({ type: z.literal("showEstimate") }),
  z.object({ type: z.literal("sitOut") }),
  z.object({ type: z.literal("sitIn") }),
  z.object({ type: z.literal("leave") }),
  z.object({ type: z.literal("rematch") }),
  z.object({ type: z.literal("ping") }),
]);
export type ClientMessage = z.infer<typeof clientMessage>;

// ---------------------------------------------------------------------------
// WebSocket: Server → Browser

export interface RoomState {
  code: string;
  /** Spieler mit offener Verbindung. */
  connected: PlayerId[];
}

export type ServerMessage =
  | { type: "state"; room: RoomState; view: GameView; events: GameEvent[]; serverTime: number }
  | { type: "error"; code: string }
  | { type: "pong"; serverTime: number };

/** Schließcodes der WebSocket-Verbindung (4000–4999 sind für Anwendungen reserviert). */
export const CLOSE_CODES = {
  roomNotFound: 4004,
  invalidSession: 4001,
  replaced: 4009,
  left: 4010,
} as const;
